import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class SeraColorShapeSelector extends HTMLElement {
  /** @type {AbortController|null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#bindToggle(signal);
    this.#bindSample(signal);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindToggle(signal) {
    const toggle = this.querySelector('[data-sera-toggle]');
    if (!(toggle instanceof HTMLButtonElement)) return;

    const count = Number(this.dataset.swatchCount) || 0;
    const colorsText = this.dataset.colorsText || 'Colors';

    toggle.addEventListener(
      'click',
      () => {
        const expanded = this.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        toggle.textContent = expanded
          ? toggle.dataset.closeText || 'Show Less'
          : `${toggle.dataset.openText || 'View All'} ${count} ${colorsText}`;
      },
      { signal }
    );
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindSample(signal) {
    const button = this.querySelector('[data-sera-sample-add]');
    if (!(button instanceof HTMLButtonElement)) return;

    button.addEventListener('click', () => this.#addSample(button), { signal });
  }

  /**
   * @param {HTMLButtonElement} button
   */
  async #addSample(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const status = this.querySelector('[data-sera-sample-status]');
    const addedLabel = button.dataset.addedText || 'Added';
    const defaultLabel = button.dataset.defaultText || button.textContent || 'Add Sample to Cart';

    const blockReason = await this.#sampleLimitReason(button);
    if (blockReason) {
      if (status instanceof HTMLElement) {
        status.textContent = blockReason;
        status.classList.add('is-error');
      }
      return;
    }

    button.classList.add('is-loading');
    button.disabled = true;
    if (status instanceof HTMLElement) {
      status.textContent = '';
      status.classList.remove('is-error');
    }

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const sectionIds = [
        ...document.querySelectorAll('cart-items-component[data-section-id]'),
      ]
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
        .filter(Boolean);

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });

      const data = await response.json();

      if (data.status) {
        const message = data.message || data.description || 'Add to cart failed';
        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );

        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            source: 'sera-color-shape-selector',
            itemCount: 1,
          },
        });

        if (status instanceof HTMLElement) {
          status.textContent = message;
          status.classList.add('is-error');
        }
        return;
      }

      const cart = await this.#refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'sera-color-shape-selector',
          itemCount: 1,
          sections: data.sections,
        },
      });

      button.textContent = addedLabel;
      setTimeout(() => {
        button.textContent = defaultLabel;
      }, 1800);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Network error during add to cart';
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      if (status instanceof HTMLElement) {
        status.textContent = message || 'Could not add sample';
        status.classList.add('is-error');
      }
    } finally {
      button.classList.remove('is-loading');
      button.disabled = button.dataset.available !== 'true';
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @returns {Promise<string>}
   */
  async #sampleLimitReason(button) {
    const productId = button.dataset.productId || '';
    /** @type {{ items?: Array<{ handle?: string, product_id?: number, quantity?: number }> }} */
    let cart;
    try {
      cart = await this.#refreshCart();
    } catch {
      return 'Could not add sample';
    }

    const items = Array.isArray(cart.items) ? cart.items : [];
    const samples = items.filter((item) => typeof item.handle === 'string' && item.handle.includes('-sample'));
    const sameProductQty = samples
      .filter((item) => productId && String(item.product_id) === productId)
      .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    const totalQty = samples.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

    if (sameProductQty + 1 > 4) return 'Maximum 4 samples per product.';
    if (totalQty + 1 > 5) return 'Maximum 5 samples.';

    return '';
  }

  /**
   * @returns {Promise<object>}
   */
  async #refreshCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      credentials: 'same-origin',
    });
    return response.json();
  }
}

if (!customElements.get('sera-color-shape-selector')) {
  customElements.define('sera-color-shape-selector', SeraColorShapeSelector);
}
